// index.ts

/**
 * SP判定用ヘルパー
 */
const isSP = (): boolean => {
  return window.matchMedia('(max-width: 767.98px)').matches;
};

/**
 * ヘッダー機能の初期化（ハンバーガーメニュー ＆ アコーディオン）
 */
function initHeader(): void {
  const hamburgerBtns = document.querySelectorAll('.js-hamburger');
  const drawer = document.querySelector('.js-drawer');

  // 親メニュー（アコーディオンのトリガーとなる要素）
  const navItemsWithChild = document.querySelectorAll('.header__nav--item--has--child');

  // リサイズ検知用の変数（横幅のみを監視するため）
  let lastInnerWidth = window.innerWidth;

  let lastToggleTimestamp = 0;
  const GUARD_DURATION_MS = 100;

  function openDrawer(btn: Element): void {
    if (!drawer) return;
    btn.classList.add('is-active');
    drawer.classList.add('is-active');
    btn.setAttribute('aria-expanded', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer(btn: Element): void {
    if (!drawer) return;
    btn.classList.remove('is-active');
    drawer.classList.remove('is-active');
    btn.setAttribute('aria-expanded', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    navItemsWithChild.forEach(el => el.classList.remove('is-open'));
  }

  hamburgerBtns.forEach(btn => {
    btn.addEventListener('click', (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      const currentBtn = e.currentTarget as Element;
      if (!drawer) return;

      const isOpen = drawer.classList.contains('is-active');
      if (isOpen) {
        hamburgerBtns.forEach(b => closeDrawer(b));
      } else {
        openDrawer(currentBtn);
      }
    });
  });

  navItemsWithChild.forEach(item => {
    const triggerLink = item.querySelector('a');

    if (triggerLink) {
      triggerLink.addEventListener('click', (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        lastToggleTimestamp = Date.now();

        const isOpen = item.classList.contains('is-open');

        if (!isSP() && !isOpen) {
          navItemsWithChild.forEach(el => {
            if (el !== item) el.classList.remove('is-open');
          });
        }

        item.classList.toggle('is-open');
      });
    }
  });

  document.addEventListener('click', (e: Event) => {
    if (Date.now() - lastToggleTimestamp < GUARD_DURATION_MS) return;

    const target = e.target as HTMLElement;

    if (!isSP()) {
      if (!target.closest('.header__nav--item--has--child')) {
        navItemsWithChild.forEach(el => el.classList.remove('is-open'));
      }
    }

    if (isSP() && drawer && drawer.classList.contains('is-active')) {
      const clickedInsideDrawer = target.closest('.js-drawer');
      const clickedHamburger = target.closest('.js-hamburger');

      if (!clickedInsideDrawer && !clickedHamburger) {
        hamburgerBtns.forEach(btn => closeDrawer(btn));
      }
    }
  });

  window.addEventListener('resize', () => {
    const currentWidth = window.innerWidth;

    if (currentWidth === lastInnerWidth) return;

    lastInnerWidth = currentWidth;

    navItemsWithChild.forEach(el => el.classList.remove('is-open'));

    if (!isSP() && drawer && drawer.classList.contains('is-active')) {
      hamburgerBtns.forEach(btn => closeDrawer(btn));
    }
  });
}

/**
 * Swiperの初期化
 */
const initFrontContentsSlider = (): void => {
  const swiperContainer = document.querySelector(".front-contents__article--wrap.swiper") as HTMLElement | null;

  // @ts-ignore
  if (swiperContainer && typeof Swiper !== 'undefined') {
    // @ts-ignore
    new Swiper(".front-contents__article--wrap.swiper", {
      loop: false,
      slidesPerView: "auto",
      spaceBetween: 0,
      centeredSlides: false,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
      autoHeight: true,
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
      breakpoints: {
        768: {
          slidesPerView: 3,
          spaceBetween: 0,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
        },
      },
    });
  }
};

/**
 * フロントページ メインセクションの表示監視
 */
function initFrontMainObserver(): void {
  const frontMain = document.querySelector<HTMLElement>('[data-front-main]');
  if (!frontMain) return;

  const OUT_CLASS = 'is-front-main-out';

  const computeOut = (): void => {
    const rect = frontMain.getBoundingClientRect();
    const inView = rect.bottom > 0 && rect.top < window.innerHeight;
    document.body.classList.toggle(OUT_CLASS, !inView);
  };

  computeOut();

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        const inView = entries.some(e => e.isIntersecting);
        document.body.classList.toggle(OUT_CLASS, !inView);
      },
      {
        root: null,
        threshold: 0.01
      }
    );

    io.observe(frontMain);

    window.addEventListener('pageshow', computeOut);
    window.addEventListener('resize', computeOut);
  } else {
    window.addEventListener('scroll', computeOut, { passive: true });
    window.addEventListener('resize', computeOut);
  }
}

/**
 * ページタイトルアニメーション
 */
function initPageTitleAnimation(): void {
  const targets = document.querySelectorAll<HTMLElement>('.js-page-title-anim');
  if (!targets.length) return;

  const show = (el: HTMLElement): void => {
    el.classList.add('is-show');
  };

  if (!('IntersectionObserver' in window)) {
    targets.forEach(show);
    return;
  }

  const io = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        show(entry.target as HTMLElement);
        observer.unobserve(entry.target);
      });
    },
    {
      root: null,
      threshold: 0.15,
      rootMargin: '0px 0px -10% 0px'
    }
  );

  targets.forEach(el => io.observe(el));
}

/**
 * パンくずリストのカテゴリーリンク書き換え
 */
function initBreadcrumbLinkRewrite(): void {
  const singleNews = document.querySelector('.single-news');
  if (!singleNews) return;

  const breadcrumbLinks = document.querySelectorAll<HTMLAnchorElement>('.breadcrumbs a');
  const baseUrl = document.body.dataset.homeUrl || '/';
  const newsUrl = `${baseUrl}news/`;

  breadcrumbLinks.forEach(link => {
    const href = link.getAttribute('href');
    if (href && href.includes('/category/')) {
      const match = href.match(/\/category\/([^\/]+)/);
      if (match && match[1]) {
        const slug = match[1];
        link.setAttribute('href', `${newsUrl}?cat=${slug}`);
      }
    }
  });
}

/**
 * 表示名を変換する関数
 */
function getDisplayName(slug: string, originalName: string): string {
  const nameMap: Record<string, string> = {
    'press': 'プレスリリース',
  };
  return nameMap[slug] || originalName;
}

/**
 * カテゴリーナビ（サーバー絞り込み前提）
 * - クリックで必ず遷移させる（親要素の preventDefault に負けないよう capture で奪う）
 * - URLパラメータから is-active と 見出しを反映する
 */
function initCategoryFilter(): void {
  const isNewsPage = document.querySelector('.archive-news') !== null;
  const isContentsPage = document.querySelector('.archive-contents') !== null;

  if (!isNewsPage && !isContentsPage) return;

  const navLinks = document.querySelectorAll<HTMLAnchorElement>('.c-cat-nav__link');
  const navItems = document.querySelectorAll<HTMLElement>('.c-cat-nav__item');

  if (!navLinks.length || !navItems.length) return;

  // 見出し要素
  const heading = isNewsPage
    ? document.getElementById('js-news-heading')
    : document.getElementById('js-contents-heading');

  // contents は h2 内に div を持っている構造なので、そこも対応
  const contentsHeadingInner =
    isContentsPage ? heading?.querySelector<HTMLElement>('.archive-contents--main--all--link') : null;

  const setHeadingText = (text: string) => {
    if (!heading) return;
    if (isContentsPage && contentsHeadingInner) {
      contentsHeadingInner.textContent = text;
    } else {
      heading.textContent = text;
    }
  };

  // -------------------------
  // 初期表示：URL param から反映
  // -------------------------
  const urlParams = new URLSearchParams(window.location.search);
  const catParam = urlParams.get('cat') || 'all';

  // active 反映
  navItems.forEach(item => item.classList.remove('is-active'));
  navLinks.forEach(link => {
    const slug = link.getAttribute('data-target') || 'all';
    if (slug === catParam) {
      link.closest<HTMLElement>('.c-cat-nav__item')?.classList.add('is-active');
    }
  });

  // 見出し反映
  if (catParam === 'all') {
    setHeadingText('すべて見る');
  } else {
    // ナビから表示名を拾う
    let foundName = '';
    navLinks.forEach(link => {
      const slug = link.getAttribute('data-target') || 'all';
      if (slug !== catParam) return;
      const nameEl = link.querySelector<HTMLElement>('.c-cat-nav__text');
      foundName = nameEl?.textContent || '';
    });
    setHeadingText(getDisplayName(catParam, foundName || catParam));
  }

  // -------------------------
  // クリック：必ず遷移させる（capture で奪う）
  // -------------------------
  navLinks.forEach(link => {
    link.addEventListener(
      'click',
      (e: MouseEvent) => {
        const href = link.getAttribute('href') || '';

        // 新規タブ/別操作は殺さない（ctrl/cmd/shift/中クリック）
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

        // ここで遷移を確実にする
        e.preventDefault();
        e.stopPropagation();
        // stopImmediatePropagation が使える環境なら確実に止める
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (e as any).stopImmediatePropagation?.();

        if (!href) return;

        // 強制遷移（親の preventDefault に負けない）
        window.location.assign(href);
      },
      { capture: true }
    );
  });
}

/**
 * スクロールアニメーション初期化
 */
function initScrollAnimation(): void {
  // ✅ プレビュー時はアニメーション無効（消える事故を潰す）
  if (document.body.classList.contains('is-preview')) return;

  // IntersectionObserver が無いなら「表示」に倒す（事故防止）
  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("visible");
        observer.unobserve(entry.target); // ✅ 一度表示したら解除（負荷軽減）
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: "0px 0px -150px 0px"
  });

  const isFront = document.querySelector(".front") !== null;

  // ✅ 非フロントで「全section」対象は危険。まずは entry-content 配下に限定する
  const selector = isFront
    ? [
        ".front-contents__header",
        ".front-contents__article--content",
        ".front-service",
        ".front-service__intro",
        ".front-service__links",
        ".front-news__wrap",
        ".front-about",
        ".front-about__card",
      ].join(", ")
    : ".entry-content > *";

  const targets = document.querySelectorAll<HTMLElement>(selector);
  targets.forEach((el) => {
    el.classList.add("fade-in");
    observer.observe(el);
  });
}


/**
 * アプリケーション初期化実行関数
 */
function init(): void {
  initHeader();                // ヘッダー・アコーディオン
  initFrontContentsSlider();   // Swiper
  initFrontMainObserver();     // メインビジュアル監視
  initPageTitleAnimation();    // タイトルアニメーション
  initBreadcrumbLinkRewrite(); // パンくず書き換え
  initCategoryFilter();        // カテゴリ（サーバー前提）
  initScrollAnimation();       // スクロールフェードイン
}

// DOMContentLoaded で init を一度だけ実行
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export {
  initFrontMainObserver,
  initPageTitleAnimation,
  initBreadcrumbLinkRewrite,
  initCategoryFilter
};